import React, { useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

// ═══════════════════════════════════════════════════════════
// StripePaymentModal — formulaire de carte Stripe (Payment Element)
// À placer dans src/components/
//
// Props :
//   clePublique   : clé publishable Stripe (pk_test_... / pk_live_...)
//   clientSecret  : secret du PaymentIntent (fourni par le backend)
//   plan          : { libelle, prix, devise } — pour l'affichage
//   onSuccess()   : appelé quand le paiement est confirmé côté Stripe
//   onClose()     : ferme la modale
//
// Le paiement est CONFIRMÉ ici, mais l'abonnement n'est activé qu'après
// vérification côté backend (appelée dans onSuccess par le parent).
// ═══════════════════════════════════════════════════════════

// Formulaire interne (doit être enfant de <Elements> pour accéder à useStripe)
const FormulairePaiement = ({ plan, onSuccess, onClose }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [enCours, setEnCours] = useState(false);
    const [erreur, setErreur] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return; // Stripe.js pas encore chargé

        setEnCours(true);
        setErreur('');

        // Confirme le paiement sans redirection (on gère le résultat ici)
        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            redirect: 'if_required',
        });

        if (error) {
            // Carte refusée, informations invalides, etc.
            setErreur(error.message || 'Le paiement a échoué. Vérifiez vos informations.');
            setEnCours(false);
            return;
        }

        if (paymentIntent && paymentIntent.status === 'succeeded') {
            // Paiement accepté par Stripe → le parent va le faire vérifier au backend
            onSuccess(paymentIntent.id);
        } else {
            setErreur('Le paiement n\'a pas abouti. Merci de réessayer.');
            setEnCours(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <div style={styles.recap}>
                <span style={styles.recapPlan}>{plan?.libelle}</span>
                <span style={styles.recapPrix}>
                    {Number(plan?.prix).toFixed(2).replace('.', ',')} {plan?.devise === 'EUR' ? '€' : plan?.devise}
                </span>
            </div>

            <div style={styles.elementBox}>
                <PaymentElement />
            </div>

            {erreur && <div style={styles.erreur}>{erreur}</div>}

            <div style={styles.actions}>
                <button type="button" onClick={onClose} disabled={enCours} style={styles.annuler}>
                    Annuler
                </button>
                <button type="submit" disabled={!stripe || enCours} style={{
                    ...styles.payer,
                    opacity: (!stripe || enCours) ? 0.6 : 1,
                    cursor: (!stripe || enCours) ? 'not-allowed' : 'pointer',
                }}>
                    {enCours ? 'Paiement en cours...' : 'Payer maintenant'}
                </button>
            </div>

            <p style={styles.secure}>
                🔒 Paiement sécurisé par Stripe. Vos informations bancaires ne transitent
                jamais par nos serveurs.
            </p>
        </form>
    );
};

const StripePaymentModal = ({ clePublique, clientSecret, plan, onSuccess, onClose }) => {
    // loadStripe est mis en cache : on ne recharge pas Stripe.js à chaque rendu
    const [stripePromise] = useState(() => loadStripe(clePublique));

    const options = {
        clientSecret,
        appearance: {
            theme: 'flat',
            variables: {
                colorPrimary: '#8b1a4a',
                colorText: '#4a0028',
                borderRadius: '10px',
                fontFamily: 'system-ui, sans-serif',
            },
        },
    };

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <h3 style={styles.titre}>Finaliser votre abonnement</h3>

                {clientSecret ? (
                    <Elements stripe={stripePromise} options={options}>
                        <FormulairePaiement plan={plan} onSuccess={onSuccess} onClose={onClose} />
                    </Elements>
                ) : (
                    <p style={{ textAlign: 'center', color: '#9a7d88' }}>Chargement du paiement...</p>
                )}
            </div>
        </div>
    );
};

const styles = {
    overlay: {
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '16px', boxSizing: 'border-box',
    },
    modal: {
        backgroundColor: '#fff', borderRadius: '18px', padding: '28px',
        width: '100%', maxWidth: '440px', boxSizing: 'border-box',
        boxShadow: '0 20px 60px rgba(0,0,0,0.35)', maxHeight: '90vh', overflowY: 'auto',
    },
    titre: {
        color: '#4a0028', fontSize: '20px', fontWeight: '800',
        margin: '0 0 20px', textAlign: 'center',
    },
    recap: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 16px', background: 'rgba(201,168,76,0.1)',
        border: '1px solid #ecd9b9', borderRadius: '12px', marginBottom: '20px',
    },
    recapPlan: { color: '#8b1a4a', fontWeight: '700', fontSize: '15px' },
    recapPrix: { color: '#4a0028', fontWeight: '800', fontSize: '18px' },
    elementBox: { marginBottom: '18px' },
    erreur: {
        backgroundColor: '#ffe0e0', color: '#c00', padding: '10px 12px',
        borderRadius: '8px', fontSize: '13px', marginBottom: '16px', textAlign: 'center',
    },
    actions: { display: 'flex', gap: '12px' },
    annuler: {
        flex: 1, padding: '13px', backgroundColor: '#fff', color: '#8b1a4a',
        border: '1.5px solid #e6cfa8', borderRadius: '10px', fontWeight: '700',
        fontSize: '14px', cursor: 'pointer',
    },
    payer: {
        flex: 2, padding: '13px', background: 'linear-gradient(135deg, #4a0028, #8b1a4a)',
        color: '#f3e0c7', border: 'none', borderRadius: '10px', fontWeight: '700', fontSize: '14px',
    },
    secure: {
        color: '#9a7d88', fontSize: '11.5px', textAlign: 'center',
        margin: '16px 0 0', lineHeight: '1.5',
    },
};

export default StripePaymentModal;